/**
 * Color mapping for metadata categories
 * Used to highlight files in Reports view and AI chat
 */

export const METADATA_COLORS = {
  'Labs': '#3182CE',           // Blue
  'Images': '#38A169',         // Green
  'Medical Records': '#DD6B20', // Orange
  'Other': '#718096'           // Gray
};

/**
 * Get color for a metadata category
 * @param {string} category - The metadata category name
 * @returns {string} - Hex color code
 */
export const getMetadataColor = (category) => {
  return METADATA_COLORS[category] || '#718096'; // Default gray
};

/**
 * Get background color (lighter shade) for a metadata category
 * @param {string} category - The metadata category name
 * @returns {string} - Hex color code for background
 */
export const getMetadataBgColor = (category) => {
  const baseColor = getMetadataColor(category);
  
  // Convert hex to RGB and lighten it
  const hex = baseColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Lighten by mixing with white (80% white, 20% color)
  const lightR = Math.round(r * 0.2 + 255 * 0.8);
  const lightG = Math.round(g * 0.2 + 255 * 0.8);
  const lightB = Math.round(b * 0.2 + 255 * 0.8);
  
  return `rgb(${lightR}, ${lightG}, ${lightB})`;
};

/**
 * Check if a category should be highlighted (Labs, Images, Medical Records)
 * @param {string} category - The metadata category name
 * @returns {boolean}
 */
export const isHighlightedCategory = (category) => {
  return ['Labs', 'Images', 'Medical Records'].includes(category);
};

/**
 * Colour for the Orders badge.
 *
 * Deliberately NOT a METADATA_COLORS key: that map is classification -> colour,
 * `getMetadataColor` is only ever called with a classification, and Orders is an
 * orthogonal flag (`is_order`), not a classification. Adding a key would also be
 * picked up by the `.slice(0, 3)` legend renders in ResultsTable/AdminPage.
 *
 * Violet: distinct from Labs blue / Images green / Medical Records orange, and not
 * the grey that reads as Other/Unclassified.
 */
export const ORDERS_COLOR = '#805AD5';

/** Light tint of ORDERS_COLOR, matching getMetadataBgColor's 20/80 mix. */
export const getOrdersBgColor = () => {
  const hex = ORDERS_COLOR.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return `rgb(${Math.round(r * 0.2 + 255 * 0.8)}, ${Math.round(g * 0.2 + 255 * 0.8)}, ${Math.round(b * 0.2 + 255 * 0.8)})`;
};

